import { useState, useEffect, useMemo, useCallback } from "react";
import {
  FlaskConical,
  Plus,
  Search,
  Copy,
  Check,
  Eye,
  EyeOff,
  Trash2,
  Edit2,
  Shield,
  Smartphone,
  Key,
  Building,
  UserCheck,
  Sparkles,
  RefreshCw,
  DoorOpen,
  Laptop,
} from "lucide-react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { toast } from "react-toastify";
import {
  getPropertyTesters,
  createPropertyTester,
  updatePropertyTester,
  deletePropertyTester,
} from "../../services/testerUserService";
import { getFlatsBySociety } from "../../services/flatsService";
import masterService from "../../services/masterEntitiesService";
import { normalizePropertyType, PROPERTY_CATEGORIES } from "../../utils/propertyStructure";
import { generateDeskCode, generateCabinCode } from "../../utils/codeGenerator";
import styles from "./PropertyTesters.module.css";

const PropertyTesters = ({ societyId, propertyType, propertyName }) => {
  const propertyId = Number(societyId);
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const [testers, setTesters] = useState([]);
  const [flats, setFlats] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [revealedOtps, setRevealedOtps] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTester, setEditingTester] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const initialFormState = {
    full_name: "",
    mobile_number: "",
    email_id: "",
    primary_role: isIndustrial ? "Director" : isCommercial ? "Commercial Owner" : "Owner",
    custom_role: "",
    tester_otp: "123456",
    flat_id: "",
    department_id: "",
    sitting_type: isIndustrial ? "CABIN" : "DESK",
    desk_code: "",
    cabin_code: "",
    designation: isIndustrial ? "Director" : "",
    notes: "",
    is_active: true,
  };
  const [formData, setFormData] = useState(initialFormState);

  // Existing Codes extracted for sequential auto-generation
  const existingDeskCodes = useMemo(() => {
    return (testers || [])
      .map((t) => (typeof t.role_details === "object" && t.role_details?.desk_code) || "")
      .filter(Boolean);
  }, [testers]);

  const existingCabinCodes = useMemo(() => {
    return (testers || [])
      .map((t) => (typeof t.role_details === "object" && t.role_details?.cabin_code) || "")
      .filter(Boolean);
  }, [testers]);

  const getSelectedDeptName = useCallback(
    (deptId, role) => {
      if (deptId) {
        const found = departments.find(
          (d) => String(d.id || d.department_id) === String(deptId)
        );
        if (found?.name) return found.name;
        if (found?.code) return found.code;
      }
      return role || "GEN";
    },
    [departments]
  );

  // Role Options fetched dynamically from DB & Settings, tailored to archetype
  const roleOptions = useMemo(() => {
    const dbRoleNames = (availableRoles || []).map((r) => r.name || r).filter(Boolean);
    if (dbRoleNames.length > 0) {
      const uniqueRoles = Array.from(new Set(dbRoleNames));
      return [...uniqueRoles, "Custom..."];
    }

    // Comprehensive fallback if network is offline
    if (isIndustrial) {
      return [
        "Director",
        "Head of Department (HOD)",
        "Plant Manager",
        "Team Lead (TL)",
        "Employee",
        "Floor Receptionist",
        "Gate Receptionist",
        "Security Guard",
        "Support Staff",
        "Custom...",
      ];
    }
    if (isCommercial) {
      return [
        "Commercial Owner",
        "Commercial Tenant",
        "Office Manager",
        "Director",
        "Head of Department (HOD)",
        "Employee",
        "Floor Receptionist",
        "Gate Receptionist",
        "Security Guard",
        "Support Staff",
        "Custom...",
      ];
    }
    return [
      "Owner",
      "Tenant",
      "Security Guard",
      "Support Staff",
      "Custom...",
    ];
  }, [availableRoles, isIndustrial, isCommercial]);

  // Load Data
  const loadData = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [testersRes, flatsData, deptsData, rolesData] = await Promise.all([
        getPropertyTesters(propertyId).catch(() => ({ data: [] })),
        getFlatsBySociety(propertyId).catch(() => []),
        masterService.getDepartments(propertyId).catch(() => []),
        masterService.getRoles({ propertyType: normalizedCategory }).catch(() => []),
      ]);

      const testersList = Array.isArray(testersRes)
        ? testersRes
        : testersRes?.data || [];

      setTesters(testersList);
      setFlats(Array.isArray(flatsData) ? flatsData : []);
      setDepartments(Array.isArray(deptsData) ? deptsData : []);
      setAvailableRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      console.error("Failed to load testers data:", err);
    } finally {
      setLoading(false);
    }
  }, [propertyId, normalizedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Suggest next test mobile number (e.g. 9800000001, 9800000002, ...)
  const suggestNextMobile = () => {
    const existingMobiles = new Set(
      testers.map((t) => String(t.mobile_number).slice(-10))
    );
    let counter = 1;
    while (counter < 1000) {
      const candidate = `9800000${String(counter).padStart(3, "0")}`;
      if (!existingMobiles.has(candidate)) {
        return candidate;
      }
      counter++;
    }
    return "9800000999";
  };

  const handleSelectSitting = (type) => {
    const deptName = getSelectedDeptName(formData.department_id, formData.primary_role);
    let nextDesk = formData.desk_code;
    let nextCabin = formData.cabin_code;

    if (type === "DESK") {
      nextDesk = nextDesk || generateDeskCode(deptName, existingDeskCodes);
      nextCabin = "";
    } else if (type === "CABIN") {
      nextCabin = nextCabin || generateCabinCode(deptName, existingCabinCodes);
      nextDesk = "";
    } else if (type === "BOTH") {
      nextDesk = nextDesk || generateDeskCode(deptName, existingDeskCodes);
      nextCabin = nextCabin || generateCabinCode(deptName, existingCabinCodes);
    } else if (type === "FLOAT") {
      nextDesk = "";
      nextCabin = "";
    }

    setFormData((prev) => ({
      ...prev,
      sitting_type: type,
      desk_code: nextDesk,
      cabin_code: nextCabin,
    }));
  };

  const handleDepartmentChange = (e) => {
    const deptId = e.target.value;
    const deptName = getSelectedDeptName(deptId, formData.primary_role);

    let nextDesk = formData.desk_code;
    let nextCabin = formData.cabin_code;

    if (formData.sitting_type === "DESK" || formData.sitting_type === "BOTH") {
      nextDesk = generateDeskCode(deptName, existingDeskCodes);
    }
    if (formData.sitting_type === "CABIN" || formData.sitting_type === "BOTH") {
      nextCabin = generateCabinCode(deptName, existingCabinCodes);
    }

    setFormData((prev) => ({
      ...prev,
      department_id: deptId,
      desk_code: nextDesk,
      cabin_code: nextCabin,
    }));
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    const roleLower = String(newRole).toLowerCase();
    let newSitting = formData.sitting_type;

    if (roleLower.includes("director") || roleLower.includes("hod") || roleLower.includes("head") || roleLower.includes("manager")) {
      newSitting = "CABIN";
    } else if (roleLower.includes("guard") || roleLower.includes("staff")) {
      newSitting = "FLOAT";
    } else if (!roleLower.includes("custom")) {
      newSitting = "DESK";
    }

    const deptName = getSelectedDeptName(formData.department_id, newRole);
    let nextDesk = formData.desk_code;
    let nextCabin = formData.cabin_code;

    if (newSitting === "DESK") {
      nextDesk = nextDesk || generateDeskCode(deptName, existingDeskCodes);
      nextCabin = "";
    } else if (newSitting === "CABIN") {
      nextCabin = nextCabin || generateCabinCode(deptName, existingCabinCodes);
      nextDesk = "";
    } else if (newSitting === "FLOAT") {
      nextDesk = "";
      nextCabin = "";
    }

    setFormData((prev) => ({
      ...prev,
      primary_role: newRole,
      sitting_type: newSitting,
      desk_code: nextDesk,
      cabin_code: nextCabin,
      designation: roleLower.includes("director") ? "Director" : (newRole !== "Custom..." ? newRole : prev.designation),
    }));
  };

  const handleRegenerateDeskCode = () => {
    const deptName = getSelectedDeptName(formData.department_id, formData.primary_role);
    const codes = existingDeskCodes.concat(formData.desk_code ? [formData.desk_code] : []);
    const nextDesk = generateDeskCode(deptName, codes);
    setFormData((prev) => ({ ...prev, desk_code: nextDesk }));
    toast.info(`Regenerated Desk Code: ${nextDesk}`);
  };

  const handleRegenerateCabinCode = () => {
    const deptName = getSelectedDeptName(formData.department_id, formData.primary_role);
    const codes = existingCabinCodes.concat(formData.cabin_code ? [formData.cabin_code] : []);
    const nextCabin = generateCabinCode(deptName, codes);
    setFormData((prev) => ({ ...prev, cabin_code: nextCabin }));
    toast.info(`Regenerated Cabin Code: ${nextCabin}`);
  };

  const handleOpenAddModal = () => {
    setEditingTester(null);
    const defaultRole = roleOptions.find((r) => r !== "Custom...") || (isIndustrial ? "Director" : "Owner");
    const defaultRoleLower = defaultRole.toLowerCase();
    const defaultSitting = (defaultRoleLower.includes("director") || defaultRoleLower.includes("cabin") || defaultRoleLower.includes("hod") || defaultRoleLower.includes("manager"))
      ? "CABIN"
      : (defaultRoleLower.includes("guard") || defaultRoleLower.includes("staff"))
      ? "FLOAT"
      : "DESK";

    const deptName = defaultRole;
    const initialDesk = defaultSitting === "DESK" ? generateDeskCode(deptName, existingDeskCodes) : "";
    const initialCabin = defaultSitting === "CABIN" ? generateCabinCode(deptName, existingCabinCodes) : "";

    setFormData({
      ...initialFormState,
      primary_role: defaultRole,
      sitting_type: defaultSitting,
      desk_code: initialDesk,
      cabin_code: initialCabin,
      designation: defaultRoleLower.includes("director") ? "Director" : defaultRole,
      mobile_number: suggestNextMobile(),
      tester_otp: "123456",
    });
    setModalOpen(true);
  };

  const handleOpenEditModal = (tester) => {
    setEditingTester(tester);
    const existingMapping = tester.user_flat_mapping?.[0];
    const isStandardRole = roleOptions.includes(tester.primary_role);
    const rd =
      typeof tester.role_details === "object" && tester.role_details !== null
        ? tester.role_details
        : {};

    const desk = rd.desk_code || "";
    const cabin = rd.cabin_code || "";
    let sitting = rd.sitting_type;
    if (!sitting) {
      if (desk && cabin) sitting = "BOTH";
      else if (cabin) sitting = "CABIN";
      else if (desk) sitting = "DESK";
      else sitting = "FLOAT";
    }

    setFormData({
      full_name: tester.full_name || "",
      mobile_number: tester.mobile_number || "",
      email_id: tester.email_id || "",
      primary_role: isStandardRole ? tester.primary_role : "Custom...",
      custom_role: isStandardRole ? "" : tester.primary_role,
      tester_otp: tester.tester_otp || rd.tester_otp || "123456",
      flat_id: existingMapping?.flat_id ? String(existingMapping.flat_id) : "",
      department_id: rd.department_id ? String(rd.department_id) : "",
      sitting_type: sitting,
      desk_code: desk,
      cabin_code: cabin,
      designation: rd.designation || "",
      notes: rd.notes || "",
      is_active: tester.is_active !== false,
    });
    setModalOpen(true);
  };

  const handleGenerateRandomOtp = () => {
    const randomOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setFormData((prev) => ({ ...prev, tester_otp: randomOtp }));
  };

  const handleToggleReveal = (userId) => {
    setRevealedOtps((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleCopyCredentials = (tester) => {
    const text = `Mobile: ${tester.mobile_number} | OTP: ${tester.tester_otp || "123456"}`;
    navigator.clipboard.writeText(text);
    setCopiedId(tester.user_id);
    toast.success(`Copied: ${text}`);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      toast.error("Please enter a persona / full name.");
      return;
    }
    const cleanMobile = String(formData.mobile_number).replace(/\D/g, "").slice(-10);
    if (cleanMobile.length !== 10) {
      toast.error("A valid 10-digit mobile number is required.");
      return;
    }
    const cleanOtp = String(formData.tester_otp || "123456").trim();
    if (!cleanOtp) {
      toast.error("Please provide a static login OTP.");
      return;
    }

    const resolvedRole =
      formData.primary_role === "Custom..."
        ? formData.custom_role.trim() || "Tester"
        : formData.primary_role;

    const payload = {
      full_name: formData.full_name.trim(),
      mobile_number: cleanMobile,
      email_id: formData.email_id.trim() || undefined,
      primary_role: resolvedRole,
      tester_otp: cleanOtp,
      flat_id: formData.flat_id ? Number(formData.flat_id) : undefined,
      department_id: formData.department_id ? Number(formData.department_id) : undefined,
      sitting_type: formData.sitting_type,
      desk_code:
        formData.sitting_type === "DESK" || formData.sitting_type === "BOTH"
          ? formData.desk_code.trim().toUpperCase() || undefined
          : undefined,
      cabin_code:
        formData.sitting_type === "CABIN" || formData.sitting_type === "BOTH"
          ? formData.cabin_code.trim().toUpperCase() || undefined
          : undefined,
      designation: formData.designation.trim() || undefined,
      notes: formData.notes.trim() || undefined,
      is_active: formData.is_active,
    };

    setSubmitting(true);
    try {
      if (editingTester) {
        await updatePropertyTester(propertyId, editingTester.user_id, payload);
      } else {
        await createPropertyTester(propertyId, payload);
      }
      setModalOpen(false);
      await loadData();
    } catch (err) {
      // Error toasted in service
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tester) => {
    if (
      !window.confirm(
        `Are you sure you want to remove tester persona "${tester.full_name}" (${tester.mobile_number})?`
      )
    ) {
      return;
    }
    try {
      await deletePropertyTester(propertyId, tester.user_id);
      await loadData();
    } catch (err) {
      // Error toasted in service
    }
  };

  // Filtered Testers
  const filteredTesters = useMemo(() => {
    return testers.filter((t) => {
      const matchSearch =
        !searchQuery ||
        (t.full_name && t.full_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (t.mobile_number && t.mobile_number.includes(searchQuery)) ||
        (t.primary_role && t.primary_role.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchRole =
        roleFilter === "all" ||
        t.primary_role?.toLowerCase() === roleFilter.toLowerCase();

      return matchSearch && matchRole;
    });
  }, [testers, searchQuery, roleFilter]);

  // Quick stats
  const activeCount = testers.filter((t) => t.is_active !== false).length;
  const mappedUnitsCount = testers.filter(
    (t) => t.user_flat_mapping && t.user_flat_mapping.length > 0
  ).length;

  return (
    <div className={styles.container}>
      {/* Hero / Explainer Banner */}
      <div className={styles.heroBanner}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <FlaskConical size={14} />
            <span>Developer & QA Sandbox</span>
          </div>
          <h3 className={styles.heroTitle}>App Tester Personas — {propertyName || "Property"}</h3>
          <p className={styles.heroSubtitle}>
            Simulate Flutter mobile app logins with pre-configured static OTPs (e.g. <code>123456</code>).
            Telecom SMS dispatch is completely bypassed for these accounts. Testers can be bound to any
            unit, role, or department in this property.
          </p>
        </div>
        <div className={styles.heroActions}>
          <Button onClick={handleOpenAddModal} size="md">
            <Plus size={16} style={{ marginRight: 6 }} />
            New Tester Persona
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIconWrap}>
            <Smartphone size={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{testers.length}</span>
            <span className={styles.statLabel}>Configured Testers</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconWrapGreen}`}>
            <UserCheck size={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{activeCount}</span>
            <span className={styles.statLabel}>Active Mobile Logins</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconWrapAmber}`}>
            <Key size={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>123456</span>
            <span className={styles.statLabel}>Default Static OTP</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={`${styles.statIconWrap} ${styles.statIconWrapPurple}`}>
            <Building size={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{mappedUnitsCount}</span>
            <span className={styles.statLabel}>Bound to Units</span>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className={styles.controlsBar}>
        <div className={styles.searchAndFilter}>
          <div className={styles.searchWrap}>
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search by persona name, test mobile, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={styles.roleSelect}
          >
            <option value="all">All Roles</option>
            {roleOptions
              .filter((r) => r !== "Custom...")
              .map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
          </select>
        </div>

        <div>
          <Button variant="outline" onClick={loadData} size="sm" title="Refresh">
            <RefreshCw size={14} style={{ marginRight: 6 }} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tester Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <RefreshCw size={24} className="spin" />
            </div>
            <p className={styles.emptyTitle}>Loading tester personas...</p>
          </div>
        ) : filteredTesters.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIconWrap}>
              <FlaskConical size={26} />
            </div>
            <h4 className={styles.emptyTitle}>No tester personas found</h4>
            <p className={styles.emptyDesc}>
              Create a test user persona with a static OTP to test mobile app logins for this property
              without requiring telecom SMS OTP delivery.
            </p>
            <Button onClick={handleOpenAddModal} size="sm">
              <Plus size={15} style={{ marginRight: 6 }} />
              Create First Tester Persona
            </Button>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Persona / Name</th>
                <th>Test Mobile</th>
                <th>Property Role</th>
                <th>Linked Unit / Space</th>
                <th>Static Login OTP</th>
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTesters.map((tester) => {
                const isRevealed = revealedOtps.has(tester.user_id);
                const isCopied = copiedId === tester.user_id;
                const mapping = tester.user_flat_mapping?.[0];
                const flat = mapping?.flats;
                const towerName = flat?.floors?.towers?.tower_name;
                const unitLabel = flat
                  ? `${flat.flat_number} ${towerName ? `(${towerName})` : ""}`
                  : null;

                const roleLower = String(tester.primary_role || "").toLowerCase();
                const roleClass = roleLower.includes("guard")
                  ? styles.roleBadgeGuard
                  : roleLower.includes("director") || roleLower.includes("admin")
                  ? styles.roleBadgeExecutive
                  : styles.roleBadgeResident;

                return (
                  <tr key={tester.user_id}>
                    <td>
                      <div className={styles.personaCell}>
                        <div className={styles.personaAvatar}>
                          {(tester.full_name || "T")[0].toUpperCase()}
                        </div>
                        <div>
                          <div className={styles.personaName}>
                            {tester.full_name}
                            {tester.role_details?.designation && (
                              <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "normal", marginLeft: "6px" }}>
                                ({tester.role_details.designation})
                              </span>
                            )}
                          </div>
                          <div className={styles.personaEmail}>{tester.email_id}</div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className={styles.mobileCell}>
                        <span>{tester.mobile_number}</span>
                        <button
                          type="button"
                          className={styles.copyIconButton}
                          onClick={() => handleCopyCredentials(tester)}
                          title="Copy Mobile & OTP"
                        >
                          {isCopied ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>

                    <td>
                      <span className={`${styles.roleBadge} ${roleClass}`}>
                        <Shield size={12} />
                        {tester.primary_role || "Owner"}
                      </span>
                    </td>

                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
                        {unitLabel ? (
                          <span className={styles.unitBadge}>
                            <Building size={12} />
                            {unitLabel}
                          </span>
                        ) : (
                          <span className={styles.emptyUnits}>No Unit</span>
                        )}
                        {tester.role_details?.cabin_code && (
                          <span className={styles.cabinBadge} title="Assigned Private Cabin">
                            <DoorOpen size={11} /> {tester.role_details.cabin_code}
                          </span>
                        )}
                        {tester.role_details?.desk_code && (
                          <span className={styles.deskBadge} title="Assigned Workstation / Desk">
                            <Laptop size={11} /> {tester.role_details.desk_code}
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={styles.otpBadge}>
                        <span>{isRevealed ? tester.tester_otp || "123456" : "••••••"}</span>
                        <button
                          type="button"
                          className={styles.revealBtn}
                          onClick={() => handleToggleReveal(tester.user_id)}
                          title={isRevealed ? "Hide OTP" : "Reveal OTP"}
                        >
                          {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`${styles.statusPill} ${
                          tester.is_active !== false
                            ? styles.statusPillActive
                            : styles.statusPillInactive
                        }`}
                      >
                        {tester.is_active !== false ? "Active" : "Disabled"}
                      </span>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <div className={styles.actionCell} style={{ justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          className={styles.editBtn}
                          onClick={() => handleOpenEditModal(tester)}
                          title="Edit Tester Persona"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={() => handleDelete(tester)}
                          title="Delete Tester Persona"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / Edit Tester Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingTester ? "Edit Tester Persona" : "Create App Tester Persona"}
        size="lg"
      >
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                Persona / Full Name <span className={styles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Rahul Sharma (QA Owner)"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, full_name: e.target.value }))
                }
                className={styles.fieldInput}
                required
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                Test Mobile Number <span className={styles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                maxLength={10}
                placeholder="e.g. 9800000001"
                value={formData.mobile_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    mobile_number: e.target.value.replace(/\D/g, ""),
                  }))
                }
                className={styles.fieldInput}
                required
              />
              <span className={styles.fieldHint}>
                Use sequential 9800000xxx numbers for easy test persona separation.
              </span>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                Static Login OTP <span className={styles.requiredStar}>*</span>
              </label>
              <div className={styles.otpInputRow}>
                <input
                  type="text"
                  maxLength={10}
                  placeholder="123456"
                  value={formData.tester_otp}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, tester_otp: e.target.value }))
                  }
                  className={styles.fieldInput}
                  required
                />
                <button
                  type="button"
                  onClick={handleGenerateRandomOtp}
                  className={styles.generateBtn}
                  title="Generate Random OTP"
                >
                  <Sparkles size={14} />
                  Random
                </button>
              </div>
              <span className={styles.fieldHint}>
                This static code will bypass SMS delivery during mobile login.
              </span>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                Property Role <span className={styles.requiredStar}>*</span>
              </label>
              <select
                value={formData.primary_role}
                onChange={handleRoleChange}
                className={styles.fieldSelect}
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.primary_role === "Custom..." && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>
                Custom Role Title <span className={styles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Shift Supervisor, Store Manager, VIP Resident"
                value={formData.custom_role}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, custom_role: e.target.value }))
                }
                className={styles.fieldInput}
                required
              />
            </div>
          )}

          <div className={styles.formRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Linked Unit / Space (Optional)</label>
              <select
                value={formData.flat_id}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, flat_id: e.target.value }))
                }
                className={styles.fieldSelect}
              >
                <option value="">-- No Unit Linked (Float / Guard) --</option>
                {flats.map((flat) => {
                  const flatId = flat.flat_id || flat.id;
                  const tower =
                    flat.floors?.towers?.tower_name ||
                    (flat.floor_number !== undefined ? `Floor ${flat.floor_number}` : "");
                  return (
                    <option key={flatId} value={flatId}>
                      Unit {flat.flat_number} {flat.unit_type ? `[${flat.unit_type}]` : ""}{" "}
                      {tower ? `(${tower})` : ""}
                    </option>
                  );
                })}
              </select>
            </div>

            {departments.length > 0 && (
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Department (Optional)</label>
                <select
                  value={formData.department_id}
                  onChange={handleDepartmentChange}
                  className={styles.fieldSelect}
                >
                  <option value="">-- Unassigned Department --</option>
                  {departments.map((dept) => {
                    const deptId = dept.id || dept.department_id;
                    return (
                      <option key={deptId} value={deptId}>
                        {dept.name} {dept.floor ? `(Floor: ${dept.floor})` : ""}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          {/* Sitting Arrangement Segmented Selector */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Sitting Arrangement</label>
            <div className={styles.sittingSegment}>
              <button
                type="button"
                className={`${styles.sittingOption} ${formData.sitting_type === "DESK" ? styles.sittingOptionActive : ""}`}
                onClick={() => handleSelectSitting("DESK")}
              >
                <Laptop size={14} /> Workstation / Desk
              </button>
              <button
                type="button"
                className={`${styles.sittingOption} ${formData.sitting_type === "CABIN" ? styles.sittingOptionActive : ""}`}
                onClick={() => handleSelectSitting("CABIN")}
              >
                <DoorOpen size={14} /> Private Cabin
              </button>
              <button
                type="button"
                className={`${styles.sittingOption} ${formData.sitting_type === "BOTH" ? styles.sittingOptionActive : ""}`}
                onClick={() => handleSelectSitting("BOTH")}
              >
                Both (Desk & Cabin)
              </button>
              <button
                type="button"
                className={`${styles.sittingOption} ${formData.sitting_type === "FLOAT" ? styles.sittingOptionActive : ""}`}
                onClick={() => handleSelectSitting("FLOAT")}
              >
                Floating / Float
              </button>
            </div>
          </div>

          {/* Dynamic Desk / Cabin Code inputs based on Sitting */}
          {formData.sitting_type === "DESK" && (
            <div className={styles.formRow}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Workstation / Desk Code</label>
                <div className={styles.codeRow}>
                  <input
                    type="text"
                    placeholder="e.g. MKT-WS-01"
                    value={formData.desk_code}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, desk_code: e.target.value.toUpperCase() }))
                    }
                    className={styles.fieldInput}
                    style={{ fontFamily: "monospace", fontWeight: 700 }}
                  />
                  <button
                    type="button"
                    className={styles.codeRegenerateBtn}
                    onClick={handleRegenerateDeskCode}
                    title="Regenerate next desk code"
                  >
                    <RefreshCw size={12} /> Regenerate
                  </button>
                </div>
                <span className={styles.fieldHint}>
                  Auto-generated based on department and existing allocations.
                </span>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Designation / Title (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Lead Engineer, Senior Analyst"
                  value={formData.designation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, designation: e.target.value }))
                  }
                  className={styles.fieldInput}
                />
              </div>
            </div>
          )}

          {formData.sitting_type === "CABIN" && (
            <div className={styles.formRow}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Private Cabin Code</label>
                <div className={styles.codeRow}>
                  <input
                    type="text"
                    placeholder="e.g. CAB-MKT-01"
                    value={formData.cabin_code}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, cabin_code: e.target.value.toUpperCase() }))
                    }
                    className={styles.fieldInput}
                    style={{ fontFamily: "monospace", fontWeight: 700 }}
                  />
                  <button
                    type="button"
                    className={styles.codeRegenerateBtn}
                    onClick={handleRegenerateCabinCode}
                    title="Regenerate next cabin code"
                  >
                    <RefreshCw size={12} /> Regenerate
                  </button>
                </div>
                <span className={styles.fieldHint}>
                  Auto-generated private cabin code for directors, HODs, and managers.
                </span>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Designation / Title (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Director, Plant Manager, HOD"
                  value={formData.designation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, designation: e.target.value }))
                  }
                  className={styles.fieldInput}
                />
              </div>
            </div>
          )}

          {formData.sitting_type === "BOTH" && (
            <>
              <div className={styles.formRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Workstation / Desk Code</label>
                  <div className={styles.codeRow}>
                    <input
                      type="text"
                      placeholder="e.g. MKT-WS-01"
                      value={formData.desk_code}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, desk_code: e.target.value.toUpperCase() }))
                      }
                      className={styles.fieldInput}
                      style={{ fontFamily: "monospace", fontWeight: 700 }}
                    />
                    <button
                      type="button"
                      className={styles.codeRegenerateBtn}
                      onClick={handleRegenerateDeskCode}
                    >
                      <RefreshCw size={12} />
                    </button>
                  </div>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>Private Cabin Code</label>
                  <div className={styles.codeRow}>
                    <input
                      type="text"
                      placeholder="e.g. CAB-MKT-01"
                      value={formData.cabin_code}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, cabin_code: e.target.value.toUpperCase() }))
                      }
                      className={styles.fieldInput}
                      style={{ fontFamily: "monospace", fontWeight: 700 }}
                    />
                    <button
                      type="button"
                      className={styles.codeRegenerateBtn}
                      onClick={handleRegenerateCabinCode}
                    >
                      <RefreshCw size={12} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Designation / Title (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Executive Director, Head of Engineering"
                  value={formData.designation}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, designation: e.target.value }))
                  }
                  className={styles.fieldInput}
                />
              </div>
            </>
          )}

          {formData.sitting_type === "FLOAT" && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Designation / Title (Optional)</label>
              <input
                type="text"
                placeholder="e.g. Security Guard, Field Inspector, Guest Tester"
                value={formData.designation}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, designation: e.target.value }))
                }
                className={styles.fieldInput}
              />
              <span className={styles.fieldHint}>
                Floating or roving persona with no dedicated desk or cabin assigned.
              </span>
            </div>
          )}

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Test Scenario Notes (Optional)</label>
            <textarea
              placeholder="e.g. Used for verifying VIP visitor gate approvals and delivery alerts."
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              className={styles.fieldTextarea}
            />
          </div>

          <div className={styles.modalFooter}>
            <Button
              type="button"
              variant="outline"
              onClick={() => setModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : editingTester ? "Update Persona" : "Create Tester Persona"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PropertyTesters;
